/* ─── HTML escape hatch (sub-iframe, auto-resizing) ─── */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { renderArtifactThemeStyles, type PanelMode } from '../theme';
import { usePaperTheme } from '@/hooks/useThemeFlag';
import { useArtifactSendEvent } from '../store';

function errorHandlerScript() {
  const errors: string[] = [];
  let bannerEl: HTMLDivElement | null = null;
  const MAX_ERRORS = 5;

  function showBanner() {
    if (bannerEl) bannerEl.remove();
    bannerEl = document.createElement('div');
    // The iframe carries the same `--artifact-*` palette as the parent
    // (injected via renderArtifactThemeStyles) and flips with the
    // `data-panel-mode` attribute, so referencing the danger and muted
    // tokens keeps the banner readable on both surfaces.
    bannerEl.style.cssText =
      'position:fixed;bottom:0;left:0;right:0;z-index:999999;' +
      'background:var(--artifact-muted);' +
      'border-top:1px solid var(--artifact-danger);' +
      'padding:10px 14px;' +
      'font-family:var(--artifact-font-mono,ui-monospace,SFMono-Regular,Menlo,monospace);' +
      'font-size:11px;color:var(--artifact-danger);' +
      'max-height:40%;overflow-y:auto;line-height:1.6;';
    const heading = document.createElement('div');
    heading.style.cssText =
      'font-weight:600;margin-bottom:6px;font-size:12px;color:var(--artifact-danger);';
    heading.textContent = 'Artifact Error (' + errors.length + ')';
    bannerEl.appendChild(heading);
    for (let i = 0; i < errors.length; i++) {
      const line = document.createElement('div');
      line.style.cssText =
        'white-space:pre-wrap;word-break:break-word;padding:2px 0;opacity:' +
        (i === errors.length - 1 ? '1' : '0.7') +
        ';';
      line.textContent = errors[i]!;
      bannerEl.appendChild(line);
    }
    document.body.appendChild(bannerEl);
  }

  function onError(msg: string, source: string | undefined, lineno: number | undefined, colno: number | undefined) {
    const text = msg + (source ? ' at ' + source + ':' + lineno + ':' + colno : '');
    if (errors.length < MAX_ERRORS) errors.push(text);
    showBanner();
    window.parent.postMessage({ __artifactError: text }, '*');
  }

  function onUnhandledRejection(reason: unknown) {
    const text =
      'Unhandled Promise: ' +
      (reason && typeof reason === 'object' && 'message' in reason
        ? (reason as { message: string }).message
        : String(reason));
    if (errors.length < MAX_ERRORS) errors.push(text);
    showBanner();
    window.parent.postMessage({ __artifactError: text }, '*');
  }

  window.onerror = function (msg, source, lineno, colno) {
    onError(String(msg), source, lineno, colno);
  };
  window.addEventListener('unhandledrejection', function (e) {
    onUnhandledRejection((e as PromiseRejectionEvent).reason);
  });
}

function autoResizeScript() {
  function postHeight() {
    const h = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
    window.parent.postMessage({ __artifactHeight: h }, '*');
  }

  function warmupForCapture() {
    try {
      const lazyNodes = document.querySelectorAll('img[loading="lazy"],iframe[loading="lazy"]');
      for (let i = 0; i < lazyNodes.length; i += 1) {
        (lazyNodes[i] as Element).setAttribute('loading', 'eager');
      }
    } catch (e) {
      console.error('[artifact] warmup: failed to eagerly load lazy nodes', e);
    }
    try {
      const maxY =
        Math.max(document.body.scrollHeight, document.documentElement.scrollHeight) -
        window.innerHeight;
      if (maxY > 0) {
        window.scrollTo(0, maxY);
        setTimeout(function () {
          window.scrollTo(0, 0);
          postHeight();
        }, 60);
        return;
      }
    } catch (e) {
      console.error('[artifact] warmup: failed to scroll for capture', e);
    }
    postHeight();
  }

  window.addEventListener('message', function (event) {
    if (!event || !event.data) return;
    if ((event.data as { __artifactMeasure?: boolean }).__artifactMeasure === true) {
      postHeight();
      return;
    }
    if ((event.data as { __artifactWarmup?: boolean }).__artifactWarmup === true) {
      warmupForCapture();
    }
  });

  if (document.readyState === 'complete') {
    postHeight();
  } else {
    window.addEventListener('load', postHeight);
  }

  if (typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(postHeight).observe(document.documentElement);
  }
  setInterval(postHeight, 500);
}

function emitBridgeScript() {
  const _modeSubs: Array<(mode: 'light' | 'dark') => void> = [];
  let _panelMode: 'light' | 'dark' =
    document.documentElement.dataset.panelMode === 'light' ? 'light' : 'dark';

  function setPanelMode(next: 'light' | 'dark') {
    if (next !== 'light' && next !== 'dark') return;
    if (_panelMode === next) return;
    _panelMode = next;
    document.documentElement.dataset.panelMode = next;
    for (let i = 0; i < _modeSubs.length; i++) {
      try { _modeSubs[i]!(next); } catch (e) { console.error('[artifact] panel mode subscriber failed', e); }
    }
  }

  (window as any).artifact = {
    emit: function (name: string, payload?: unknown) {
      window.parent.postMessage({ __artifactEvent: { name: name, payload: payload } }, '*');
    },
    get panelMode(): 'light' | 'dark' { return _panelMode; },
    onPanelModeChange: function (fn: (mode: 'light' | 'dark') => void): () => void {
      _modeSubs.push(fn);
      try { fn(_panelMode); } catch (e) { console.error('[artifact] panel mode subscriber failed', e); }
      return function () {
        const idx = _modeSubs.indexOf(fn);
        if (idx >= 0) _modeSubs.splice(idx, 1);
      };
    },
  };

  const _state: Record<string, unknown> = {};
  const _subs: Record<string, Array<(val: unknown) => void>> = {};

  (window as any).bindings = {
    get: function (key: string): unknown {
      return _state[key];
    },

    subscribe: function (key: string, fn: (val: unknown) => void): () => void {
      if (!_subs[key]) _subs[key] = [];
      _subs[key]!.push(fn);
      if (key in _state) fn(_state[key]!);
      return function () {
        const arr = _subs[key];
        if (arr) {
          const idx = arr.indexOf(fn);
          if (idx >= 0) arr.splice(idx, 1);
        }
      };
    },
  };

  window.addEventListener('message', function (e) {
    if (!e.data) return;
    if (typeof e.data.__artifactPanelMode === 'string') {
      setPanelMode(e.data.__artifactPanelMode as 'light' | 'dark');
    }
    if (!e.data.__artifactBindings) return;
    const bindings = e.data.__artifactBindings as Record<string, unknown>;
    for (const key of Object.keys(bindings)) {
      const val = bindings[key];
      if (_state[key] !== val) {
        _state[key] = val;
        const fns = _subs[key];
        if (fns) for (let i = 0; i < fns.length; i++) fns[i]!(val);
      }
    }
  });

  window.parent.postMessage({ __artifactBindingsReady: true }, '*');
}

function openLinksInParentScript() {
  document.addEventListener(
    'click',
    function (event) {
      if (event.defaultPrevented) return;
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      let target = event.target as HTMLElement | null;
      while (target && target.tagName !== 'A') target = target.parentElement;
      if (!target) return;
      const href = target.getAttribute('href');
      if (!href || href.charAt(0) === '#') return;
      event.preventDefault();
      event.stopPropagation();
      window.parent.postMessage({ __artifactOpenLink: href }, '*');
    },
    true,
  );
}

function toIIFEScriptTag(fn: () => void): string {
  return `<script>(${fn.toString()})()</script>`;
}

/* Iframe baseline. The theme block exposes the same ``--artifact-*`` CSS
   variables that the parent panel container exposes, gated on
   ``[data-panel-mode]`` so dark/light flips with a single attribute toggle.
   Authors who use ``var(--artifact-foreground)`` etc. (or who scope rules
   on ``:root[data-panel-mode="light"]``) get theme awareness for free. */
const BASE_STYLE = `<style>
${renderArtifactThemeStyles()}
  html, body {
    margin: 0; padding: 0;
    background: transparent;
    color: var(--artifact-foreground);
    font-family: var(--artifact-font);
    font-size: 14px;
    line-height: 1.5;
  }
  a { color: var(--artifact-primary); }
</style>`;
const ERROR_HANDLER_SCRIPT = toIIFEScriptTag(errorHandlerScript);
const AUTO_RESIZE_SCRIPT = toIIFEScriptTag(autoResizeScript);
const EMIT_BRIDGE_SCRIPT = toIIFEScriptTag(emitBridgeScript);
const OPEN_LINKS_IN_PARENT_SCRIPT = toIIFEScriptTag(openLinksInParentScript);

interface ArtifactHTMLProps {
  raw?: string;
  width?: string;
  height?: string;
  min_height?: string;
  aspect_ratio?: string | null;
  scrollable?: boolean;
  bindings?: Record<string, unknown>;
  __node_id?: string;
}

export function ArtifactHTML({
  raw = '',
  width = '100%',
  height: heightProp = 'auto',
  min_height = '200px',
  aspect_ratio = null,
  scrollable = true,
  bindings,
  __node_id,
}: ArtifactHTMLProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const sendEvent = useArtifactSendEvent();
  const autoResize = heightProp === 'auto' && !aspect_ratio;
  const [measuredHeight, setMeasuredHeight] = useState(300);
  const [iframeErrors, setIframeErrors] = useState<string[]>([]);
  const bindingsRef = useRef(bindings);
  const resizeTimersRef = useRef<Array<ReturnType<typeof setTimeout>>>([]);
  bindingsRef.current = bindings;

  // Track the parent's coarse light/dark choice. We mirror it into the
  // iframe via postMessage rather than re-rendering srcDoc, so the
  // iframe never reloads on a theme toggle — it just flips a single
  // ``data-panel-mode`` attribute on its <html> element.
  const paperTheme = usePaperTheme();
  const panelMode: PanelMode = useMemo(
    () => (paperTheme === 'paper-light' ? 'light' : 'dark'),
    [paperTheme],
  );
  const panelModeRef = useRef<PanelMode>(panelMode);
  panelModeRef.current = panelMode;

  const clearResizeTimers = useCallback(() => {
    for (const timer of resizeTimersRef.current) clearTimeout(timer);
    resizeTimersRef.current = [];
  }, []);

  const dispatchIframeResize = useCallback(() => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    try {
      const event = iframeRef.current?.contentDocument?.createEvent('Event') ?? new Event('resize');
      event.initEvent('resize', false, false);
      win.dispatchEvent(event);
    } catch {
      // Ignore detached or inaccessible frames.
    }
  }, []);

  const handleIframeLoad = useCallback(() => {
    clearResizeTimers();
    for (const delay of [0, 50, 250, 1000]) {
      resizeTimersRef.current.push(setTimeout(dispatchIframeResize, delay));
    }
  }, [clearResizeTimers, dispatchIframeResize]);

  const handleMessage = useCallback((e: MessageEvent) => {
    if (!e.data || !iframeRef.current || e.source !== iframeRef.current.contentWindow) return;

    if (typeof e.data.__artifactOpenLink === 'string') {
      const href = e.data.__artifactOpenLink as string;
      try {
        const url = new URL(href, window.location.href);
        window.open(url.href, '_blank', 'noopener,noreferrer');
      } catch { /* malformed URL – ignore */ }
      return;
    }

    if (e.data.__artifactEvent && typeof e.data.__artifactEvent === 'object') {
      const { name, payload } = e.data.__artifactEvent as { name: string; payload: unknown };
      if (__node_id) {
        sendEvent(__node_id, 'event', { kind: 'HtmlEventEmitted', name: name ?? '', payload: payload ?? null });
      }
      return;
    }

    if (typeof e.data.__artifactError === 'string') {
      const errorMsg = e.data.__artifactError as string;
      setIframeErrors((prev) => (prev.length >= 5 ? prev : [...prev, errorMsg]));
      if (__node_id) {
        sendEvent(__node_id, 'error', { kind: 'HtmlErrored', message: errorMsg });
      }
      return;
    }

    if (e.data.__artifactBindingsReady === true) {
      const win = iframeRef.current?.contentWindow;
      if (!win) return;
      win.postMessage({ __artifactPanelMode: panelModeRef.current }, '*');
      const b = bindingsRef.current;
      if (b) win.postMessage({ __artifactBindings: b }, '*');
      return;
    }

    if (
      autoResize &&
      typeof e.data.__artifactHeight === 'number'
    ) {
      const newHeight = Math.max(100, e.data.__artifactHeight);
      setMeasuredHeight((prev) => (Math.abs(prev - newHeight) > 2 ? newHeight : prev));
    }
  }, [autoResize, __node_id, sendEvent]);

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
      clearResizeTimers();
    };
  }, [clearResizeTimers, handleMessage]);

  useEffect(() => {
    setIframeErrors([]);
  }, [raw]);

  useEffect(() => {
    if (bindings && iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage({ __artifactBindings: bindings }, '*');
    }
  }, [bindings]);

  useEffect(() => {
    iframeRef.current?.contentWindow?.postMessage(
      { __artifactPanelMode: panelMode },
      '*',
    );
  }, [panelMode]);

  const srcDoc = BASE_STYLE + ERROR_HANDLER_SCRIPT + EMIT_BRIDGE_SCRIPT + raw + OPEN_LINKS_IN_PARENT_SCRIPT + (autoResize ? AUTO_RESIZE_SCRIPT : '');

  const resolvedHeight = autoResize ? measuredHeight : heightProp;

  return (
    <div className="a-html-wrapper" style={{ position: 'relative', width }}>
      <iframe
        ref={iframeRef}
        title="Artifact HTML"
        srcDoc={srcDoc}
        className="a-html-iframe"
        onLoad={handleIframeLoad}
        style={{
          width: '100%',
          height: resolvedHeight,
          minHeight: min_height,
          aspectRatio: aspect_ratio ?? undefined,
          border: 'none',
          borderRadius: 'var(--artifact-border-radius)',
          background: 'transparent',
          overflow: scrollable ? 'auto' : 'hidden',
        }}
        scrolling={scrollable ? 'auto' : 'no'}
      />
      {iframeErrors.length > 0 && (
        <div
          style={{
            marginTop: 4,
            padding: '8px 12px',
            background: 'var(--artifact-muted)',
            border: '1px solid var(--artifact-danger)',
            borderRadius: 'var(--artifact-border-radius, 8px)',
            fontFamily: 'var(--artifact-font-mono, ui-monospace, SFMono-Regular, Menlo, monospace)',
            fontSize: 11,
            color: 'var(--artifact-danger)',
            lineHeight: 1.6,
            maxHeight: 120,
            overflowY: 'auto',
          }}
        >
          <div style={{ fontWeight: 600, color: 'var(--artifact-danger)', marginBottom: 4, fontSize: 12 }}>
            Iframe Error ({iframeErrors.length})
          </div>
          {iframeErrors.map((err, i) => (
            <div key={i} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', opacity: i === iframeErrors.length - 1 ? 1 : 0.7 }}>
              {err}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
